import { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Move } from "lucide-react";

// Fix default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface LocationPickerProps {
  lat: number | null;
  lng: number | null;
  onLocationSelect: (lat: number, lng: number) => void;
  onReverseGeocode?: (lat: number, lng: number) => void;
  className?: string;
}

export const LocationPicker = ({ lat, lng, onLocationSelect, onReverseGeocode, className = "" }: LocationPickerProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  // Keep latest callbacks without re-creating Leaflet handlers / map on each render
  const onLocationSelectRef = useRef<LocationPickerProps["onLocationSelect"]>(onLocationSelect);
  const onReverseGeocodeRef = useRef<LocationPickerProps["onReverseGeocode"]>(onReverseGeocode);

  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
  }, [onLocationSelect]);

  useEffect(() => {
    onReverseGeocodeRef.current = onReverseGeocode;
  }, [onReverseGeocode]);

  const hasValidCoords = lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng) && 
    lat >= 49 && lat <= 55 && lng >= 14 && lng <= 24;

  const updateLocation = useCallback((newLat: number, newLng: number) => {
    // Check if coordinates are within Poland bounds
    if (newLat >= 49 && newLat <= 55 && newLng >= 14 && newLng <= 24) {
      const lat = parseFloat(newLat.toFixed(6));
      const lng = parseFloat(newLng.toFixed(6));
      onLocationSelectRef.current(lat, lng);
      // Trigger reverse geocoding callback
      const reverse = onReverseGeocodeRef.current;
      if (reverse) reverse(lat, lng);
    }
  }, []);

  const handleMapClick = useCallback((e: L.LeafletMouseEvent) => {
    updateLocation(e.latlng.lat, e.latlng.lng);
  }, [updateLocation]);

  const handleMarkerDragEnd = useCallback((e: L.DragEndEvent) => {
    const marker = e.target as L.Marker;
    const position = marker.getLatLng();
    updateLocation(position.lat, position.lng);
  }, [updateLocation]);

  useEffect(() => {
    if (!mapRef.current) return;

    // Initialize map only once
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [52.0, 19.0], // Center of Poland
        zoom: 6,
        zoomControl: true,
        scrollWheelZoom: true,
        dragging: true,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(mapInstanceRef.current);

      // Add click handler
      mapInstanceRef.current.on('click', handleMapClick);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off('click', handleMapClick);
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [handleMapClick]);

  // Update marker when coordinates change
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.off('dragend', handleMarkerDragEnd);
      markerRef.current.remove();
      markerRef.current = null;
    }

    if (hasValidCoords && lat && lng) {
      // Add new draggable marker
      markerRef.current = L.marker([lat, lng], { 
        draggable: true,
        autoPan: true,
      }).addTo(mapInstanceRef.current);
      
      // Add drag end handler
      markerRef.current.on('dragend', handleMarkerDragEnd);
      
      // Add tooltip
      markerRef.current.bindTooltip("Przeciągnij aby zmienić lokalizację", {
        direction: 'top',
        offset: [0, -40],
      });
      
      mapInstanceRef.current.setView([lat, lng], Math.max(mapInstanceRef.current.getZoom(), 10));
    }
  }, [lat, lng, hasValidCoords, handleMarkerDragEnd]);

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="w-3 h-3" />
          <span>Wybierz lokalizację</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-primary">
          <Move className="w-3 h-3" />
          <span>Kliknij lub przeciągnij pinezkę</span>
        </div>
      </div>
      <div 
        ref={mapRef} 
        className="h-64 w-full rounded-lg border bg-muted overflow-hidden cursor-crosshair"
        style={{ minHeight: "256px" }}
      />
      {hasValidCoords ? (
        <p className="text-xs text-primary bg-primary/10 p-2 rounded text-center">
          ✓ Wybrana lokalizacja: {lat?.toFixed(4)}, {lng?.toFixed(4)}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground text-center">
          Kliknij na mapę aby wybrać lokalizację lub użyj geokodowania
        </p>
      )}
    </div>
  );
};
